import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ExclamationCircleIcon,
  PhotoIcon,
  BoldIcon,
  ItalicIcon,
  ListBulletIcon,
  NumberedListIcon,
  DocumentArrowUpIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { usePressRelease } from '../../contexts/PressReleaseContext';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import FontSize from '../../extensions/fontSize';
import { uploadImageToStorage } from '../../utils/imageUpload';
import { supabase } from '../../lib/supabase';

interface PressReleaseFormData {
  title: string;
  department: string;
  content: string;
  is_published: boolean;
  author: string;
  thumbnail_url: string | null;
  file_url: string | null;
  file_name: string | null;
  file_size: number | null;
}

const PressReleaseForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addRelease, updateRelease, getRelease, refreshReleases } = usePressRelease();
  const isEditMode = !!id;

  const [formData, setFormData] = useState<PressReleaseFormData>({
    title: '',
    department: '교육운영팀',
    content: '',
    is_published: true,
    author: '관리자',
    thumbnail_url: null,
    file_url: null,
    file_name: null,
    file_size: null
  });

  const [errors, setErrors] = useState<Partial<Record<keyof PressReleaseFormData, string>>>({});
  const [uploading, setUploading] = useState(false);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      Color.configure({
        types: ['textStyle'],
      }),
      FontSize,
      Placeholder.configure({
        placeholder: '보도자료 내용을 입력하세요...',
      }),
      Image.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: 'editor-image',
        },
      }),
    ],
    content: formData.content,
    onUpdate: ({ editor }) => {
      setFormData(prev => ({ ...prev, content: editor.getHTML() }));
      if (errors.content) {
        setErrors(prev => ({ ...prev, content: '' }));
      }
    },
  });

  // 에디터 내 이미지 업로드
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && editor) {
      try {
        const imageUrl = await uploadImageToStorage(file, 'press');
        editor.chain().focus().setImage({ src: imageUrl }).run();
      } catch (error) {
        console.error('Failed to upload image:', error);
        alert('이미지 업로드에 실패했습니다.');
      }
    }
  };

  // 썸네일 업로드
  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setThumbnailUploading(true);
    try {
      const imageUrl = await uploadImageToStorage(file, 'press');
      setFormData(prev => ({ ...prev, thumbnail_url: imageUrl }));
    } catch (error) {
      console.error('Failed to upload thumbnail:', error);
      alert('썸네일 업로드에 실패했습니다.');
    } finally {
      setThumbnailUploading(false);
    }
  };

  const removeThumbnail = () => {
    setFormData(prev => ({ ...prev, thumbnail_url: null }));
  };

  // 첨부파일 업로드
  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const fileName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const filePath = `press-documents/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('program-images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('program-images')
        .getPublicUrl(filePath);

      setFormData(prev => ({
        ...prev,
        file_url: publicUrl,
        file_name: file.name,
        file_size: file.size
      }));
    } catch (error) {
      console.error('Failed to upload document:', error);
      alert('파일 업로드에 실패했습니다.');
    } finally {
      setUploading(false);
    }
  };

  const removeDocument = () => {
    setFormData(prev => ({
      ...prev,
      file_url: null,
      file_name: null,
      file_size: null
    }));
  };

  const addImageFromButton = () => {
    fileInputRef.current?.click();
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof PressReleaseFormData]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Partial<Record<keyof PressReleaseFormData, string>> = {};

    if (!formData.title) newErrors.title = '제목은 필수입니다.';
    if (!formData.department) newErrors.department = '담당부서는 필수입니다.';
    if (!formData.content || formData.content === '<p></p>') newErrors.content = '내용은 필수입니다.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        if (isEditMode && id) {
          await updateRelease(Number(id), formData);
          alert('보도자료가 성공적으로 수정되었습니다!');
        } else {
          await addRelease(formData);
          alert('보도자료가 성공적으로 등록되었습니다!');
        }
        navigate('/admin/press');
      } catch (error) {
        console.error('Failed to submit press release:', error);
        alert('저장에 실패했습니다.');
      }
    }
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Edit mode: Load existing data
  useEffect(() => {
    const loadRelease = async () => {
      if (isEditMode && id) {
        await refreshReleases();
        const release = getRelease(Number(id));
        if (release) {
          setFormData({
            title: release.title,
            department: release.department,
            content: release.content,
            is_published: release.is_published,
            author: release.author,
            thumbnail_url: release.thumbnail_url,
            file_url: release.file_url,
            file_name: release.file_name,
            file_size: release.file_size
          });
          if (editor) {
            editor.commands.setContent(release.content);
          }
        }
      }
    };
    loadRelease();
  }, [isEditMode, id, editor]);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          {isEditMode ? '보도자료 수정' : '보도자료 등록'}
        </h1>
        <p className="text-gray-600">
          {isEditMode ? '보도자료 내용을 수정합니다.' : '새로운 보도자료를 등록합니다.'}
          <span className="text-red-500"> *</span> 표시는 필수 항목입니다.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-8">
        {/* 기본 정보 */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">기본 정보</h2>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                제목 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  errors.title ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="보도자료 제목을 입력하세요"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.title}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                담당부서 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  errors.department ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="예: 교육운영팀"
              />
              {errors.department && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.department}
                </p>
              )}
            </div>

            <div>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_published"
                  checked={formData.is_published}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      is_published: e.target.checked
                    }));
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">공개</span>
              </label>
              <p className="mt-1 text-xs text-gray-500 ml-6">체크하면 사용자에게 공개됩니다.</p>
            </div>

            {/* 썸네일 이미지 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                썸네일 이미지
              </label>
              <p className="text-xs text-gray-500 mb-2">목록에서 표시될 대표 이미지입니다. (사진, 포스터 등)</p>
              {formData.thumbnail_url ? (
                <div className="relative inline-block">
                  <img
                    src={formData.thumbnail_url}
                    alt="썸네일 미리보기"
                    className="w-48 h-36 object-cover rounded-lg border border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={removeThumbnail}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div>
                  <input
                    ref={thumbnailInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleThumbnailUpload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => thumbnailInputRef.current?.click()}
                    disabled={thumbnailUploading}
                    className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <PhotoIcon className="h-5 w-5 mr-2" />
                    {thumbnailUploading ? '업로드 중...' : '썸네일 선택'}
                  </button>
                </div>
              )}
            </div>

            {/* 첨부파일 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                첨부파일
              </label>
              {formData.file_url ? (
                <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <DocumentArrowUpIcon className="h-5 w-5 text-blue-500" />
                    <span className="text-sm text-gray-700">{formData.file_name}</span>
                    <span className="text-xs text-gray-400">({formatFileSize(formData.file_size)})</span>
                  </div>
                  <button
                    type="button"
                    onClick={removeDocument}
                    className="p-1 text-gray-400 hover:text-red-500"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              ) : (
                <div>
                  <input
                    ref={documentInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.hwp,.zip"
                    onChange={handleDocumentUpload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => documentInputRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
                    {uploading ? '업로드 중...' : '파일 선택'}
                  </button>
                  <p className="mt-1 text-xs text-gray-500">PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, HWP, ZIP 파일 지원</p>
                </div>
              )}
            </div>

            {/* 내용 에디터 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                내용 <span className="text-red-500">*</span>
              </label>
              <div className={`border rounded-lg ${errors.content ? 'border-red-500' : 'border-gray-300'} bg-white`}>
                {/* 에디터 툴바 */}
                <div className="flex items-center gap-0.5 p-2 border-b border-gray-200">
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleBold().run()}
                    className={`p-2 rounded hover:bg-gray-100 transition-colors ${
                      editor?.isActive('bold') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Bold"
                  >
                    <BoldIcon className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleItalic().run()}
                    className={`p-2 rounded hover:bg-gray-100 transition-colors ${
                      editor?.isActive('italic') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Italic"
                  >
                    <ItalicIcon className="h-5 w-5" />
                  </button>

                  <div className="w-px h-6 bg-gray-300 mx-1"></div>

                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                    className={`px-2.5 py-1.5 rounded hover:bg-gray-100 transition-colors font-semibold text-sm ${
                      editor?.isActive('heading', { level: 2 }) ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Heading 2"
                  >
                    H2
                  </button>
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
                    className={`px-2.5 py-1.5 rounded hover:bg-gray-100 transition-colors font-semibold text-sm ${
                      editor?.isActive('heading', { level: 3 }) ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Heading 3"
                  >
                    H3
                  </button>

                  <div className="w-px h-6 bg-gray-300 mx-1"></div>

                  {/* 폰트 크기 */}
                  <select
                    value={editor?.getAttributes('textStyle').fontSize || ''}
                    onChange={(e) => {
                      if (!editor) return;
                      const size = e.target.value;
                      if (size) {
                        editor.chain().focus().setFontSize(size).run();
                      } else {
                        editor.chain().focus().unsetFontSize().run();
                      }
                    }}
                    className="px-2 py-1 text-sm border border-gray-300 rounded hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-green-500 bg-white text-gray-600"
                    title="글자 크기"
                  >
                    <option value="">기본</option>
                    <option value="12px">12px 작게</option>
                    <option value="14px">14px 보통</option>
                    <option value="18px">18px 크게</option>
                    <option value="20px">20px 매우 크게</option>
                    <option value="24px">24px 특대</option>
                  </select>

                  <div className="w-px h-6 bg-gray-300 mx-1"></div>

                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleBulletList().run()}
                    className={`p-2 rounded hover:bg-gray-100 transition-colors ${
                      editor?.isActive('bulletList') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Bullet List"
                  >
                    <ListBulletIcon className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                    className={`p-2 rounded hover:bg-gray-100 transition-colors ${
                      editor?.isActive('orderedList') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Numbered List"
                  >
                    <NumberedListIcon className="h-5 w-5" />
                  </button>

                  <div className="w-px h-6 bg-gray-300 mx-1"></div>

                  <button
                    type="button"
                    onClick={addImageFromButton}
                    className="p-2 rounded hover:bg-gray-100 transition-colors text-gray-600"
                    title="Insert Image"
                  >
                    <PhotoIcon className="h-5 w-5" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />

                  <div className="w-px h-6 bg-gray-300 mx-1"></div>

                  {/* 색상 버튼들 */}
                  {[
                    { color: '#000000', title: '검정색' },
                    { color: '#6B7280', title: '회색' },
                    { color: '#EF4444', title: '빨간색' },
                    { color: '#F97316', title: '주황색' },
                    { color: '#EAB308', title: '노란색' },
                    { color: '#22C55E', title: '초록색' },
                    { color: '#059669', title: '에메랄드' },
                    { color: '#14B8A6', title: '청록색' },
                    { color: '#3B82F6', title: '파란색' },
                    { color: '#8B5CF6', title: '보라색' },
                  ].map(({ color, title }) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => {
                        if (!editor) return;
                        const { from, to } = editor.state.selection;
                        const text = editor.state.doc.textBetween(from, to, '');
                        if (text) {
                          editor.chain().focus().deleteSelection().insertContent(`<span style="color: ${color}">${text}</span>`).run();
                        }
                      }}
                      className="w-5 h-5 rounded border-2 border-gray-300 hover:border-gray-500 hover:scale-110 transition-all"
                      style={{ backgroundColor: color }}
                      title={title}
                    />
                  ))}
                </div>
                {/* 에디터 본문 */}
                <EditorContent
                  editor={editor}
                  className="prose max-w-none p-4 min-h-[300px] focus:outline-none"
                />
              </div>
              {errors.content && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.content}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* 버튼 */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/admin/press')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            취소
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            {isEditMode ? '수정하기' : '등록하기'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PressReleaseForm;
